import type { ReactNode } from 'react';

export type WorkspaceSectionTone = 'neutral' | 'attention' | 'ready';

export type WorkspaceSectionItem<SectionId extends string> = {
  id: SectionId;
  label: string;
  icon?: ReactNode;
  badge?: ReactNode;
  tone?: WorkspaceSectionTone;
};

/**
 * Return focus to the section control that remains visible after an overview
 * route replaces the button that launched it. Wide workspaces keep the active
 * button; compact workspaces replace that whole button row with a select.
 */
export function focusActiveWorkspaceSection(root: ParentNode | null) {
  const navigation = root?.querySelector<HTMLElement>('.workspace-section-nav');
  const activeButton = navigation?.querySelector<HTMLElement>('[aria-current="page"]');
  const compactSelect = navigation?.querySelector<HTMLSelectElement>(
    '.workspace-section-select select',
  );
  (activeButton?.getClientRects().length ? activeButton : compactSelect)?.focus();
}

/**
 * The shared local navigation for configuration and administration workspaces.
 *
 * These are deliberately buttons, rather than links or ARIA tabs: switching a
 * section changes the composition of one already-open task and does not create
 * browser history. The compact select is the same control at narrow widths, so
 * it never turns into a horizontally scrolling strip of clipped labels.
 */
export function WorkspaceSectionNav<SectionId extends string>({
  telemetry,
  label,
  items,
  active,
  onChange,
}: {
  /** The navigation's code name for product telemetry: each button is `<telemetry>-<section id>`, never its label. */
  telemetry: 'settings-nav' | 'setup-nav';
  label: string;
  items: readonly WorkspaceSectionItem<SectionId>[];
  active: SectionId;
  onChange: (section: SectionId) => void;
}) {
  const badgeFor = (item: WorkspaceSectionItem<SectionId>) =>
    item.badge !== undefined && item.badge !== null && item.badge !== ''
      ? item.badge
      : item.tone === 'attention'
        ? 'Needs attention'
        : null;
  return (
    <div className="workspace-section-nav">
      <nav className="workspace-section-buttons" aria-label={label}>
        {items.map((item) => {
          const badge = badgeFor(item);
          return (
            <button
              key={item.id}
              type="button"
              className={`workspace-section-button${active === item.id ? ' active' : ''}${item.tone ? ` ${item.tone}` : ''}`}
              aria-current={active === item.id ? 'page' : undefined}
              data-telemetry={`${telemetry}-${item.id}`}
              onClick={() => onChange(item.id)}
            >
              {item.icon && (
                <span className="workspace-section-icon" aria-hidden="true">
                  {item.icon}
                </span>
              )}
              <span>{item.label}</span>
              {badge !== null && (
                <small aria-label={`${item.label}: ${String(badge)}`}>{badge}</small>
              )}
            </button>
          );
        })}
      </nav>
      <label className="workspace-section-select">
        <span>{label}</span>
        <select
          aria-label={label}
          value={active}
          onChange={(event) => onChange(event.target.value as SectionId)}
        >
          {items.map((item) => {
            const badge = badgeFor(item);
            return (
              <option key={item.id} value={item.id}>
                {item.label}
                {badge !== null ? ` (${String(badge)})` : ''}
              </option>
            );
          })}
        </select>
      </label>
    </div>
  );
}
